import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the pc-to-darkweb start screen. Three short
 * slides (what/who, how a password gets out, key terms) shown in the reader
 * while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. breach notice: a stolen-logins list with the learner's row       */
/* ------------------------------------------------------------------ */
function NoticeArt(): JSX.Element {
  const H = 296;
  const dots = "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
  const rows: { y: number; who: string }[] = [
    { y: 148, who: "m.carter@\u2022\u2022\u2022\u2022\u2022.com" },
    { y: 170, who: "j.okafor@\u2022\u2022\u2022\u2022\u2022.com" },
  ];
  return (
    <Frame label="Example: security notice" aria="A security notice saying a work email and password were found on a list of stolen logins" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Security notice
      </text>
      <rect className="sr-anim-pulse" x="262" y="37" width="82" height="24" rx="12" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.45" style={{ transformOrigin: "303px 49px" }} />
      <text x="303" y="53.5" fontSize="11" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.6">
        FOUND
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <text x="16" y="94" fontSize="12.5" fill={C.text}>
        Your work login was found on a list
      </text>
      <text x="16" y="110" fontSize="12.5" fill={C.text}>
        of stolen logins sold online.
      </text>

      <rect x="16.5" y="122.5" width="327" height="112" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="138" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        stolen_logins.txt
      </text>
      <text x="330" y="138" fontSize="10.5" fill={C.muted} textAnchor="end">
        shared by criminals
      </text>
      <line x1="17" y1="144.5" x2="343" y2="144.5" stroke={C.line} />
      {rows.map((r) => (
        <g key={r.y}>
          <text x="29" y={r.y + 12} fontSize="11.5" fontFamily={MONO} fill={C.muted}>
            {r.who}
          </text>
          <text x="330" y={r.y + 12} fontSize="11.5" fontFamily={MONO} fill={C.muted} textAnchor="end">
            {dots}
          </text>
        </g>
      ))}
      <rect x="24.5" y="190.5" width="311" height="30" rx="6" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.45" />
      <circle className="sr-anim-ring" cx="40" cy="205.5" r="4.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="40" cy="205.5" r="1.8" fill={C.amber} />
      <text x="52" y="209.5" fontSize="11.5" fontFamily={MONO} fontWeight="700" fill={C.amber}>
        you@your-company.com
      </text>
      <text x="326" y="209.5" fontSize="11.5" fontFamily={MONO} fill={C.amber} textAnchor="end">
        {dots}
      </text>
      <rect className="sr-anim-blink" x="330" y="199" width="1.5" height="13" rx="0.75" fill={C.amber} />

      <rect x="16.5" y="246.5" width="327" height="34" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="261" fontSize="11.5" fontWeight="600" fill={C.text}>
        Not automatically your fault.
      </text>
      <text x="29" y="274" fontSize="10.5" fill={C.muted}>
        Often it leaked at another company.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. routes: four ways a password gets out, all ending at one login   */
/* ------------------------------------------------------------------ */
type Route = "page" | "malware" | "site" | "social";

function RouteGlyph({ g }: { g: Route }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "page":
      return (
        <g {...s}>
          <rect x="-8" y="-6.5" width="16" height="13" rx="2" />
          <line x1="-8" y1="-2" x2="8" y2="-2" />
          <line x1="-4" y1="2.5" x2="2" y2="2.5" />
        </g>
      );
    case "malware":
      return (
        <g {...s}>
          <rect x="-8" y="-7" width="16" height="11" rx="2" />
          <line x1="-4" y1="7.5" x2="4" y2="7.5" />
          <line x1="0" y1="4" x2="0" y2="7.5" />
        </g>
      );
    case "site":
      return (
        <g {...s}>
          <ellipse cx="0" cy="-4.5" rx="6.5" ry="2.6" />
          <path d="M-6.5 -4.5 V4.5 a6.5 2.6 0 0 0 13 0 V-4.5" />
        </g>
      );
    case "social":
      return (
        <g {...s}>
          <path d="M-8 -6.5 h16 v10 h-9 l-4 3.5 v-3.5 h-3 z" />
        </g>
      );
  }
}

const ROUTES: { g: Route; title: string; sub: string }[] = [
  { g: "page", title: "Copied login page", sub: "you type it in" },
  { g: "malware", title: "Spyware on a PC", sub: "it copies it" },
  { g: "site", title: "Hacked website", sub: "their breach" },
  { g: "social", title: "Watched or asked", sub: "you are tricked" },
];

function RoutesArt(): JSX.Element {
  const H = 296;
  const cx = 296;
  const cy = 150;
  return (
    <Frame label="How a password gets out" aria="Four routes, a copied login page, spyware, a hacked website and being watched or asked, all leading to your work password" height={H}>
      {ROUTES.map((r, i) => {
        const y = 36 + i * 54;
        const my = y + 24;
        return (
          <g key={r.g}>
            <path d={`M156 ${my} C 200 ${my}, 220 ${cy}, ${cx - 32} ${cy}`} fill="none" stroke={C.line} strokeWidth="1.5" strokeDasharray="4 4" />
            <g transform={`translate(16 ${y})`}>
              <rect x="0.5" y="0.5" width="139" height="47" rx="10" fill={C.panel2} stroke={C.line} />
              <circle cx="24" cy="24" r="15" fill={C.mint} fillOpacity="0.12" />
              <g transform="translate(24 24)">
                <RouteGlyph g={r.g} />
              </g>
              <text x="46" y="21" fontSize="11" fontWeight="700" fill={C.text}>
                {r.title}
              </text>
              <text x="46" y="35" fontSize="10" fill={C.muted}>
                {r.sub}
              </text>
            </g>
          </g>
        );
      })}

      <circle className="sr-anim-ring" cx={cx} cy={cy} r="28" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="28" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.5" />
      <g transform={`translate(${cx} ${cy - 2})`} fill="none" stroke={C.red} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="-8" y="-1" width="16" height="11" rx="2.4" />
        <path d="M-4.5 -1 V-5 a4.5 4.5 0 0 1 9 0 V-1" />
      </g>
      <text x={cx} y={cy + 48} fontSize="11.5" fontWeight="700" fill={C.text} textAnchor="middle">
        Your work login
      </text>
      <text x={cx} y={cy + 62} fontSize="10" fill={C.muted} textAnchor="middle">
        one route is enough
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. one password, many doors: reuse versus an extra sign-in check    */
/* ------------------------------------------------------------------ */
function DoorsArt(): JSX.Element {
  const H = 296;
  const rows: { y: number; name: string; note: string; ok: boolean }[] = [
    { y: 100, name: "Work email", note: "unique + extra check", ok: true },
    { y: 150, name: "Shared files", note: "unique + extra check", ok: true },
    { y: 200, name: "Old shop login", note: "same password reused", ok: false },
  ];
  return (
    <Frame label="One stolen password, tried everywhere" aria="A stolen password tried on three accounts: two with an extra sign-in check are blocked, one that reuses the password is let in" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="12" fontFamily={MONO} fill={C.text}>
        Tester1!
      </text>
      <rect className="sr-anim-blink" x="104" y="40" width="1.5" height="14" rx="0.75" fill={C.mint} />
      <text x="344" y="53" fontSize="10.5" fill={C.muted} textAnchor="end">
        stolen password
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      {rows.map((r) => {
        const col = r.ok ? C.mint : C.red;
        return (
          <g key={r.y}>
            <rect x="16.5" y={r.y - 12.5} width="327" height="40" rx="8" fill={C.panel2} stroke={C.line} />
            <text x="30" y={r.y + 4} fontSize="12.5" fontWeight="700" fill={C.text}>
              {r.name}
            </text>
            <text x="30" y={r.y + 18} fontSize="10" fill={C.muted}>
              {r.note}
            </text>
            <rect
              className={r.ok ? undefined : "sr-anim-pulse"}
              x="254"
              y={r.y - 3}
              width="76"
              height="22"
              rx="11"
              fill={col}
              fillOpacity="0.14"
              stroke={col}
              strokeOpacity="0.5"
              style={{ transformOrigin: `292px ${r.y + 8}px` }}
            />
            <text x="292" y={r.y + 11.5} fontSize="10.5" fontWeight="700" fill={col} textAnchor="middle" letterSpacing="0.5">
              {r.ok ? "BLOCKED" : "LET IN"}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="248.5" width="327" height="32" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <text x="180" y="268.5" fontSize="12" fontWeight="600" fill={C.mint} textAnchor="middle">
        Unique password + extra check stops it
      </text>
    </Frame>
  );
}

export const PC_TO_DARKWEB_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Your password is worth money.",
    illustration: NoticeArt,
    caption: "Found on a list. Not yet used against you.",
    sections: [
      {
        h: "What this is about",
        body: "Your work login turns up on a list of stolen logins. You learn how it got out, what criminals do with it, and the habits that protect you.",
      },
      {
        h: "Who it\u2019s for",
        body: "All staff. No technical knowledge needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Criminals don\u2019t break in when they can log in. A stolen password makes them look like you, and no tool can tell the difference. What you type, click, plug in and reuse is the control. Yours.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Four ways a password gets out.",
    illustration: RoutesArt,
    caption: "Any one route is enough for a criminal.",
    sections: [
      {
        h: "A copied login page",
        body: "A fake page looks real. Type your password and it goes straight to them.",
      },
      {
        h: "Spyware on a computer",
        body: "A fake download can hide a program that quietly copies saved passwords.",
      },
      {
        h: "A site you use gets hacked",
        body: "The leak may not be your fault. If you reused the password, it opens other doors.",
      },
      {
        h: "Watched or simply asked",
        body: "Someone looks over your shoulder, or a caller asks. Never share it.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: DoorsArt,
    caption: "Reuse opens doors. An extra check closes them.",
    sections: [
      { h: "Dark web", body: "Hidden sites where criminals buy and sell stolen data, including logins." },
      { h: "Infostealer", body: "Spyware that copies saved passwords and sends them to criminals." },
      { h: "Credential stuffing", body: "Trying one stolen password on many other sites, hoping you reused it." },
      { h: "Extra sign-in check (MFA)", body: "A second proof after the password, such as a code or an approval on your phone." },
      { h: "Password manager", body: "A tool that makes unique passwords and refuses to fill a copied page." },
    ],
  },
];
